import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ChevronDown, Info, Plus, Search } from 'lucide-react';
import FormDrawer from './FormDrawer';
import { useAuth } from '../contexts/AuthContext';

const leaveCategories = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'casual', label: 'Casual Leave' },
  { value: 'medical', label: 'Medical Leave' },
];

const rangeTypes = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_week', label: 'This Week' },
  { value: 'custom', label: 'Custom Range' },
];

const emptyForm = {
  user_id: '',
  category: '',
  description: '',
  range_type: 'today',
  start_date: '',
  end_date: '',
  admin_comment: '',
};

const ELIGIBLE_ROLES = new Set(['member', 'project_lead', 'admin']);

function userDisplayName(u) {
  return (u?.full_name || u?.username || u?.email || 'User').trim();
}

/**
 * Active team users eligible for admin-created leave (members, leads, admins).
 */
function filterLeaveEligibleUsers(list = []) {
  return list
    .filter((u) => {
      if (!u?.id) return false;
      if (!ELIGIBLE_ROLES.has(u.role)) return false;
      if (u.is_disabled === true) return false;
      if (u.is_verified === false) return false;
      if (u.approval_status === 'declined') return false;
      const name = (u.full_name || '').trim();
      if (name === 'Unknown User') return false;
      return Boolean(name || u.username || u.email);
    })
    .sort((a, b) => userDisplayName(a).toLowerCase().localeCompare(userDisplayName(b).toLowerCase()));
}

/**
 * Admin-only drawer to add leave for a user.
 * Members' and leads' leave is approved right away; admins' leave (including your
 * own) is submitted to their approver. The backend decides via /approval-route.
 */
const AdminLeaveModal = ({ isOpen, onClose, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [approvalRoute, setApprovalRoute] = useState(null);
  const userDropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(emptyForm);
    setLeaveBalance(null);
    setUsersError('');
    setUserDropdownOpen(false);
    setUserSearch('');

    let cancelled = false;
    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const response = await axios.get('/api/team/active');
        if (cancelled) return;
        setUsers(filterLeaveEligibleUsers(response.data || []));
      } catch (error) {
        console.error('Failed to fetch users for admin leave:', error);
        if (!cancelled) {
          setUsers([]);
          setUsersError(error.response?.data?.detail || 'Failed to load users');
          toast.error('Failed to load team members');
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!userDropdownOpen) return undefined;
    const onDocClick = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
        setUserSearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [userDropdownOpen]);

  useEffect(() => {
    if (!isOpen || !formData.user_id) {
      setLeaveBalance(null);
      return;
    }

    let cancelled = false;
    const loadBalance = async () => {
      setBalanceLoading(true);
      try {
        const response = await axios.get('/api/leaves/balance/all');
        const match = (response.data || []).find((b) => b.user_id === formData.user_id);
        if (!cancelled) setLeaveBalance(match || null);
      } catch (error) {
        console.error('Failed to fetch leave balance:', error);
        if (!cancelled) setLeaveBalance(null);
      } finally {
        if (!cancelled) setBalanceLoading(false);
      }
    };
    loadBalance();
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.user_id]);

  useEffect(() => {
    setApprovalRoute(null);
    if (!isOpen || !formData.user_id) return undefined;

    let cancelled = false;
    axios
      .get('/api/leaves/admin/approval-route', { params: { user_id: formData.user_id } })
      .then((response) => {
        if (!cancelled) setApprovalRoute(response.data || null);
      })
      .catch((error) => {
        console.error('Failed to load leave approval route:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.user_id]);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return users;
    const term = userSearch.toLowerCase().trim();
    return users.filter((u) => userDisplayName(u).toLowerCase().includes(term));
  }, [users, userSearch]);

  const selectedUser = users.find((u) => u.id === formData.user_id);
  const isSelf = Boolean(selectedUser) && selectedUser.id === currentUser?.id;
  const selectedUserLabel = selectedUser
    ? `${userDisplayName(selectedUser)}${isSelf ? ' (you)' : ''}`
    : '';
  // Fallback while the route loads; the backend makes the final call on submit.
  const requiresApproval = approvalRoute
    ? approvalRoute.requires_approval
    : Boolean(selectedUser) && (isSelf || selectedUser.role === 'admin');

  const getCategoryRemaining = (category) => {
    if (!leaveBalance) return null;
    switch (category) {
      case 'annual':
        return { remaining: leaveBalance.annual_remaining, limit: leaveBalance.annual_limit };
      case 'casual':
        return { remaining: leaveBalance.casual_remaining, limit: leaveBalance.casual_limit };
      case 'medical':
        return { remaining: leaveBalance.medical_remaining, limit: leaveBalance.medical_limit };
      default:
        return null;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.user_id || !formData.category || !formData.description.trim() || !formData.range_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.range_type === 'custom' && (!formData.start_date || !formData.end_date)) {
      toast.error('Please select start and end dates for custom range');
      return;
    }

    if (formData.range_type === 'custom') {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (startDate > endDate) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        user_id: formData.user_id,
        category: formData.category,
        description: formData.description.trim(),
        range_type: formData.range_type,
        admin_comment: requiresApproval ? undefined : formData.admin_comment.trim() || undefined,
      };

      if (formData.range_type === 'custom') {
        payload.start_date = new Date(formData.start_date).toISOString();
        payload.end_date = new Date(formData.end_date).toISOString();
      }

      const { data } = await axios.post('/api/leaves/admin', payload);
      toast.success(
        data?.status === 'pending' ? 'Leave submitted for approval' : 'Leave added and auto-approved'
      );
      onSuccess?.();
      onClose?.();
      setFormData(emptyForm);
    } catch (error) {
      console.error('Error creating admin leave:', error);
      toast.error(error.response?.data?.detail || 'Failed to add leave');
    } finally {
      setLoading(false);
    }
  };

  const categoryInfo = formData.category ? getCategoryRemaining(formData.category) : null;
  const approverLabel = approvalRoute?.approver_name || 'another admin';

  const footer = (
    <>
      <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
        Cancel
      </button>
      <button
        type="submit"
        form="admin-leave-form"
        disabled={loading || usersLoading || users.length === 0}
        className="btn-primary min-w-[160px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            {requiresApproval ? 'Submitting…' : 'Adding…'}
          </span>
        ) : requiresApproval ? (
          'Submit for Approval'
        ) : (
          'Add & Approve Leave'
        )}
      </button>
    </>
  );

  return (
    <FormDrawer
      open={isOpen}
      onClose={onClose}
      title="Add Leave for User"
      subtitle={
        requiresApproval
          ? 'Admin leave is sent for approval.'
          : 'Approved right away for members and leads.'
      }
      icon={<Plus className="h-5 w-5" />}
      iconColor="primary"
      widthClass="sm:max-w-md lg:max-w-lg"
      footer={footer}
    >
      <form id="admin-leave-form" onSubmit={handleSubmit} className="space-y-5">
        <div className="relative" ref={userDropdownRef}>
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
            Team Member *
          </label>
          <button
            type="button"
            onClick={() => {
              if (usersLoading || users.length === 0) return;
              setUserDropdownOpen((open) => !open);
            }}
            disabled={usersLoading || users.length === 0}
            className="input-field w-full text-left flex items-center justify-between gap-2 disabled:opacity-60"
          >
            <span className={`truncate text-sm ${selectedUserLabel ? 'text-secondary-900 dark:text-secondary-100' : 'text-secondary-400'}`}>
              {usersLoading
                ? 'Loading…'
                : usersError
                  ? 'Could not load users'
                  : users.length === 0
                    ? 'No users available'
                    : selectedUserLabel || 'Select user'}
            </span>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-secondary-400 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {userDropdownOpen && (
            <div className="absolute z-40 mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-72 flex flex-col">
              <div className="p-2 border-b border-secondary-100 dark:border-secondary-700 sticky top-0 bg-white dark:bg-secondary-800 rounded-t-lg">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Search employee..."
                    className="input-field w-full !pl-9"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {filteredUsers.map((u) => {
                  const name = userDisplayName(u);
                  const selected = formData.user_id === u.id;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        handleInputChange('user_id', u.id);
                        setUserDropdownOpen(false);
                        setUserSearch('');
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        selected
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                          : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                      }`}
                    >
                      {name}
                      {u.id === currentUser?.id && (
                        <span className="text-secondary-400 dark:text-secondary-500"> (you)</span>
                      )}
                    </button>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <div className="px-4 py-3 text-sm text-secondary-500 dark:text-secondary-400">No matches</div>
                )}
              </div>
            </div>
          )}

          <input type="hidden" value={formData.user_id} required readOnly />
          {usersError && <p className="mt-1 text-xs text-red-600">{usersError}</p>}
          {requiresApproval && approvalRoute && (
            <div
              className="mt-2 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50/60 px-3 py-2 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-200"
              data-testid="admin-leave-approval-note"
            >
              <Info className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>
                {isSelf ? 'Your own leave' : `Leave for ${selectedUserLabel}`} needs approval
                from {approverLabel}.
              </span>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
            Leave Category *
          </label>
          <select
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          >
            <option value="">Select category</option>
            {leaveCategories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          {balanceLoading && formData.user_id && (
            <p className="mt-1 text-xs text-secondary-500">Loading balance…</p>
          )}
          {!balanceLoading && categoryInfo && (
            <p className="mt-1 text-xs text-secondary-600 dark:text-secondary-400">
              Remaining: {categoryInfo.remaining} / {categoryInfo.limit} days
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
            Date Range *
          </label>
          <select
            value={formData.range_type}
            onChange={(e) => handleInputChange('range_type', e.target.value)}
            className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          >
            {rangeTypes.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-secondary-500">
            {isSelf
              ? 'Your own leave follows the normal same-day cutoff.'
              : 'Same-day leave is allowed for admins even after the member cutoff.'}
          </p>
        </div>

        {formData.range_type === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                End Date *
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
            Reason / Description *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={isSelf ? 'Why do you need this leave?' : 'Why is this leave being added for the user?'}
            required
          />
        </div>

        {!requiresApproval && (
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
              Admin Note (optional)
            </label>
            <textarea
              value={formData.admin_comment}
              onChange={(e) => handleInputChange('admin_comment', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Shown on the leave record (e.g. user missed cutoff)"
            />
          </div>
        )}
      </form>
    </FormDrawer>
  );
};

export default AdminLeaveModal;
